import { useMemo, useState } from 'react';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { DataExport } from '../../../../../types/dbTypes';
import { DownloadSimple, Invoice } from '@phosphor-icons/react';
import { formatDateWithTime } from '../../../../../utils/DateUtils';
import { useQuery } from '@tanstack/react-query';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { CustomerWithDriverOnboarding } from '../../../../../types/apiTypes';
import { isPreActiveCustomer } from '../home/utils/demoData';

interface DataExportsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function DataExports({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: DataExportsProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();
  const [loadingMap, setLoadingMap] = useState<Map<string, boolean>>(new Map());
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );
  const { data: dataExports = [], isLoading } = useQuery<DataExport[]>({
    queryKey: ['dataExports', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/dataExports/${userId ?? ''}`,
        'get',
        {}
      );
      return response?.data?.data?.exports || [];
    },
    enabled: !!userId && !isPreActive,
  });

  const downloadCsv = async (dataExportId: string) => {
    setLoadingMap((prev) => new Map(prev).set(dataExportId, true));
    try {
      const response = await callProtectedApi(
        `/documents/display/dataExport/${dataExportId}`,
        'get',
        {},
        'Error occurred while downloading CSV'
      );
      const presigned_url = response?.data?.data?.presigned_url;
      if (presigned_url) {
        window.open(presigned_url, '_blank');
      }
    } catch (error: any) {
      console.error('Error occurred while downloading CSV: ', error);
    } finally {
      setLoadingMap((prev) => new Map(prev).set(dataExportId, false));
    }
  };

  const getStatusColorText = (
    is_available: boolean,
    processing_completed_timestamp: Date | null | undefined
  ) => {
    if (is_available) {
      return 'text-ctadarkgreen';
    } else if (
      !is_available &&
      (processing_completed_timestamp === null ||
        !processing_completed_timestamp)
    ) {
      return 'text-accent';
    } else {
      return 'text-danger';
    }
  };

  const getStatusColorBg = (
    is_available: boolean,
    processing_completed_timestamp: Date | null | undefined
  ) => {
    if (is_available) {
      return 'bg-ctaactivegreen';
    } else if (
      !is_available &&
      (processing_completed_timestamp === null ||
        !processing_completed_timestamp)
    ) {
      return 'bg-[#FEF6E9]';
    } else {
      return 'bg-[#FBEEEE]';
    }
  };

  const getStatusText = (
    is_available: boolean,
    processing_completed_timestamp: Date | null | undefined
  ) => {
    if (is_available) {
      return 'Available';
    } else if (
      !is_available &&
      (processing_completed_timestamp === null ||
        !processing_completed_timestamp)
    ) {
      return 'Processing';
    } else {
      return 'Failed';
    }
  };

  const getFormatBadge = (file_format: DataExport['file_format']) => {
    if (file_format === 'qbo') {
      return (
        <span className="rounded-[6px] bg-blue-100 px-2 py-[2px] text-[12px] font-semibold text-blue-700">
          QBO
        </span>
      );
    }
    if (file_format === 'ofx') {
      return (
        <span className="rounded-[6px] bg-purple-100 px-2 py-[2px] text-[12px] font-semibold text-purple-700">
          OFX
        </span>
      );
    }
    return (
      <span className="rounded-[6px] bg-gray-100 px-2 py-[2px] text-[12px] font-semibold text-gray-600">
        CSV
      </span>
    );
  };

  const getDownloadButtonLabel = (file_format: DataExport['file_format']) => {
    if (file_format === 'qbo') {
      return (
        <>
          <span>Download for</span>
          <img
            src="/quickbooks.png"
            alt="QuickBooks"
            width={73}
            height={20}
            className="h-[20px] object-contain"
          />
        </>
      );
    }
    if (file_format === 'ofx') {
      return <span>Download as OFX</span>;
    }
    return <span>Download as CSV</span>;
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <>
            <div className="flex h-full w-[1148px] max-w-[1148px] flex-col overflow-x-hidden overflow-y-hidden">
              <div className="mt-[16px] h-full flex-grow-0 overflow-y-auto rounded-t-[8px] border-[1px] border-bordergray bg-white">
                {!isLoading ? (
                  <>
                    {dataExports && dataExports?.length > 0 ? (
                      <table className="w-full min-w-full table-fixed overflow-hidden">
                        <thead className="h-[50px] max-h-[50px] bg-primarybg">
                          <tr className="h-[50px] max-h-[50px] overflow-hidden">
                            <th className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                              Created At
                            </th>
                            <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                              Export Type
                            </th>
                            <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                              Status
                            </th>
                            <th className="h-[50px] max-h-[50px] w-[100px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                              Format
                            </th>
                            <th className="h-[50px] max-h-[50px] w-[260px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                          </tr>
                        </thead>
                        <tbody className="table-fixed overflow-hidden">
                          {dataExports.map(
                            (dataExport: DataExport, index: number) => (
                              <tr
                                key={index}
                                className="h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200"
                              >
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                  {formatDateWithTime(
                                    new Date(dataExport?.created_at ?? ''),
                                    true
                                  )}
                                </td>
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                  {dataExport?.export_type &&
                                    dataExport?.export_type[0]?.toUpperCase() +
                                      dataExport?.export_type?.slice(1)}
                                </td>
                                <td
                                  className={`flex h-[50px] max-h-[50px] items-center justify-start overflow-hidden px-4 py-2 text-[14px]`}
                                >
                                  <div
                                    className={`rounded-[8px] px-3 py-1 font-semibold ${getStatusColorText(dataExport?.is_available ?? false, dataExport?.processing_completed_timestamp)} ${getStatusColorBg(dataExport?.is_available ?? false, dataExport?.processing_completed_timestamp)}`}
                                  >
                                    {getStatusText(
                                      dataExport?.is_available ?? false,
                                      dataExport?.processing_completed_timestamp
                                    )}
                                  </div>
                                </td>
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                  {getFormatBadge(dataExport?.file_format)}
                                </td>
                                <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                  <div className="flex items-center justify-start space-x-2">
                                    <AccentButtonOutline
                                      onClick={async () => {
                                        await downloadCsv(
                                          dataExport?.data_export_id
                                        );
                                      }}
                                      disabled={!dataExport?.is_available}
                                      addClass="w-[200px] px-3 py-[2px] rounded-[8px] flex items-center justify-center space-x-1 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                                    >
                                      <DownloadSimple size={16} />
                                      {getDownloadButtonLabel(
                                        dataExport?.file_format
                                      )}
                                    </AccentButtonOutline>
                                    {loadingMap.get(
                                      dataExport?.data_export_id ?? ''
                                    ) && (
                                      <ClipLoader
                                        color="#1D213B"
                                        loading={loadingMap.get(
                                          dataExport?.data_export_id ?? ''
                                        )}
                                        size={20}
                                        aria-label="Loading Spinner"
                                        data-testid="loader"
                                        className="flex h-full w-full items-center justify-center"
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-2">
                        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <Invoice size={40} className="text-graysecondary" />
                        </div>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          No Data Exports Found
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={isLoading}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full items-center justify-center"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );
      case 'tablet':
        return (
          <>
            <div className="flex h-full w-full flex-col overflow-x-hidden overflow-y-hidden pt-[16px]">
              <div className="m-[16px] h-full flex-grow-0 overflow-hidden rounded-t-[8px] border-[1px] border-bordergray bg-white">
                {!isLoading ? (
                  <>
                    {dataExports && dataExports?.length > 0 ? (
                      <div className="min-w-full overflow-auto">
                        <table className="w-full min-w-[860px] whitespace-nowrap">
                          <thead className="h-[50px] max-h-[50px] bg-primarybg">
                            <tr className="h-[50px] max-h-[50px] overflow-hidden">
                              <th className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Created At
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Export Type
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Status
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Format
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                            </tr>
                          </thead>
                          <tbody className="table-fixed overflow-hidden">
                            {dataExports.map(
                              (dataExport: DataExport, index: number) => (
                                <tr
                                  key={index}
                                  className="h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200"
                                >
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {formatDateWithTime(
                                      new Date(dataExport?.created_at ?? ''),
                                      true
                                    )}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {dataExport?.export_type &&
                                      dataExport?.export_type[0]?.toUpperCase() +
                                        dataExport?.export_type?.slice(1)}
                                  </td>
                                  <td
                                    className={`flex h-[50px] max-h-[50px] items-center justify-start overflow-hidden px-4 py-2 text-[14px]`}
                                  >
                                    <div
                                      className={`rounded-[8px] px-3 py-1 font-semibold ${getStatusColorText(dataExport?.is_available ?? false, dataExport?.processing_completed_timestamp)} ${getStatusColorBg(dataExport?.is_available ?? false, dataExport?.processing_completed_timestamp)}`}
                                    >
                                      {getStatusText(
                                        dataExport?.is_available ?? false,
                                        dataExport?.processing_completed_timestamp
                                      )}
                                    </div>
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {getFormatBadge(dataExport?.file_format)}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    <div className="flex items-center justify-start space-x-2">
                                      <AccentButtonOutline
                                        onClick={async () => {
                                          await downloadCsv(
                                            dataExport?.data_export_id
                                          );
                                        }}
                                        disabled={!dataExport?.is_available}
                                        addClass="w-[200px] px-3 py-[2px] rounded-[8px] flex items-center justify-center space-x-1 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                                      >
                                        <DownloadSimple size={16} />
                                        {getDownloadButtonLabel(
                                          dataExport?.file_format
                                        )}
                                      </AccentButtonOutline>
                                      {loadingMap.get(
                                        dataExport?.data_export_id ?? ''
                                      ) && (
                                        <ClipLoader
                                          color="#1D213B"
                                          loading={loadingMap.get(
                                            dataExport?.data_export_id ?? ''
                                          )}
                                          size={20}
                                          aria-label="Loading Spinner"
                                          data-testid="loader"
                                          className="flex h-full w-full items-center justify-center"
                                        />
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
                        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <Invoice size={40} className="text-graysecondary" />
                        </div>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          No Data Exports Found
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={isLoading}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full items-center justify-center"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );
      case 'mobile':
        return (
          <>
            <div className="flex h-full w-full flex-col overflow-x-hidden overflow-y-hidden pt-[16px]">
              <div className="m-[16px] h-full flex-grow-0 overflow-hidden rounded-t-[8px] border-[1px] border-bordergray bg-white">
                {!isLoading ? (
                  <>
                    {dataExports && dataExports?.length > 0 ? (
                      <div className="min-w-full overflow-auto">
                        <table className="w-full min-w-[860px] whitespace-nowrap">
                          <thead className="h-[50px] max-h-[50px] bg-primarybg">
                            <tr className="h-[50px] max-h-[50px] overflow-hidden">
                              <th className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Created At
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Export Type
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Status
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Format
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                            </tr>
                          </thead>
                          <tbody className="table-fixed overflow-hidden">
                            {dataExports.map(
                              (dataExport: DataExport, index: number) => (
                                <tr
                                  key={index}
                                  className="h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200"
                                >
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {formatDateWithTime(
                                      new Date(dataExport?.created_at ?? ''),
                                      true
                                    )}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {dataExport?.export_type &&
                                      dataExport?.export_type[0]?.toUpperCase() +
                                        dataExport?.export_type?.slice(1)}
                                  </td>
                                  <td
                                    className={`flex h-[50px] max-h-[50px] items-center justify-start overflow-hidden px-4 py-2 text-[14px]`}
                                  >
                                    <div
                                      className={`rounded-[8px] px-3 py-1 font-semibold ${getStatusColorText(dataExport?.is_available ?? false, dataExport?.processing_completed_timestamp)} ${getStatusColorBg(dataExport?.is_available ?? false, dataExport?.processing_completed_timestamp)}`}
                                    >
                                      {getStatusText(
                                        dataExport?.is_available ?? false,
                                        dataExport?.processing_completed_timestamp
                                      )}
                                    </div>
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {getFormatBadge(dataExport?.file_format)}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    <div className="flex items-center justify-start space-x-2">
                                      <AccentButtonOutline
                                        onClick={async () => {
                                          await downloadCsv(
                                            dataExport?.data_export_id
                                          );
                                        }}
                                        disabled={!dataExport?.is_available}
                                        addClass="w-[200px] px-3 py-[2px] rounded-[8px] flex items-center justify-center space-x-1 whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white"
                                      >
                                        <DownloadSimple size={16} />
                                        {getDownloadButtonLabel(
                                          dataExport?.file_format
                                        )}
                                      </AccentButtonOutline>
                                      {loadingMap.get(
                                        dataExport?.data_export_id ?? ''
                                      ) && (
                                        <ClipLoader
                                          color="#1D213B"
                                          loading={loadingMap.get(
                                            dataExport?.data_export_id ?? ''
                                          )}
                                          size={20}
                                          aria-label="Loading Spinner"
                                          data-testid="loader"
                                          className="flex h-full w-full items-center justify-center"
                                        />
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-2">
                        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <Invoice size={40} className="text-graysecondary" />
                        </div>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          No Data Exports Found
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={isLoading}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full items-center justify-center"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default DataExports;
